import type { ContractedBook } from '@/data/types';
import type { Tier } from '@/state/GameState';
import { sjangerVektFor, type UtenlandskForlag } from '@/data/utenlandske';

export const UTENLANDS_BASE_ENGANGS = 50_000;
export const UTENLANDS_ENGANGS_MIN = 50_000;
export const UTENLANDS_ENGANGS_MAX = 500_000;

export const UTENLANDS_ROYALTY_MIN = 30;
export const UTENLANDS_ROYALTY_MAX = 100;

export const UTENLANDS_ROYALTY_PROPORSJON = 0.3;

export const UTENLANDS_MIN_SALG_GLOBAL = 10_000;
export const UTENLANDS_MIN_SNITT_GLOBAL = 4.0;

export const UTENLANDS_TIER_MULT: Record<Tier, number> = {
  D: 0.5,
  C: 1.0,
  B: 1.5,
  A: 2.5,
  S: 4.0,
};

export type UtenlandsTilbud = {
  engangs: number;
  royalty: number;
  passer: boolean;
};

export function snittTerninger(bok: ContractedBook): number {
  if (bok.anmeldelser.length === 0) return 0;
  let sum = 0;
  for (const a of bok.anmeldelser) sum += a.terninger;
  return sum / bok.anmeldelser.length;
}

export function erKvalifisertForUtenlands(bok: ContractedBook): boolean {
  if (bok.status !== 'utgitt') return false;
  if (bok.salg < UTENLANDS_MIN_SALG_GLOBAL) return false;
  if (snittTerninger(bok) < UTENLANDS_MIN_SNITT_GLOBAL) return false;
  return true;
}

export function beregnUtenlandsTilbud(
  bok: ContractedBook,
  forlag: UtenlandskForlag,
  spillerTier: Tier = 'C',
): UtenlandsTilbud {
  const snitt = snittTerninger(bok);
  if (bok.salg < forlag.minSalg || snitt < forlag.minAnmeldelsesSnitt) {
    return { engangs: 0, royalty: 0, passer: false };
  }

  const tierMult = UTENLANDS_TIER_MULT[spillerTier] ?? 1.0;
  const salgsLog = bok.salg > 0 ? Math.log10(bok.salg) / 4 : 0;
  const anmeldelsesMult = snitt / 4;
  const sjangerMult = sjangerVektFor(forlag, bok.genre);

  const rawEngangs =
    UTENLANDS_BASE_ENGANGS *
    tierMult *
    salgsLog *
    anmeldelsesMult *
    sjangerMult;

  const engangs = Math.round(
    clamp(rawEngangs, UTENLANDS_ENGANGS_MIN, UTENLANDS_ENGANGS_MAX) / 1000,
  ) * 1000;

  const royaltySpan = UTENLANDS_ROYALTY_MAX - UTENLANDS_ROYALTY_MIN;
  const royaltyFaktor = clamp01(
    (tierMult * anmeldelsesMult * sjangerMult * (0.4 + salgsLog)) / 8,
  );
  const royalty = Math.round(
    UTENLANDS_ROYALTY_MIN + royaltySpan * royaltyFaktor,
  );

  return { engangs, royalty: clampRoyalty(royalty), passer: true };
}

function clamp(n: number, lo: number, hi: number): number {
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function clampRoyalty(n: number): number {
  return clamp(n, UTENLANDS_ROYALTY_MIN, UTENLANDS_ROYALTY_MAX);
}
